import { Banner, Box, FlexBox, Pagination } from '@dixa/lib-shared-habla';
import { sizes } from '@dixa/lib-shared-tokens';
import classnames from 'classnames';
import { usePagination } from 'react-use-pagination';

import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { elevioActions } from '../../elevio.actions';
import { elevioSelectors } from '../../elevio.reducer/selectors';
import { ArticleSuggestion } from '../../types';
import { ElevioError, getUrl, isValidSearchLength } from '../../utils';
import { ARTICLES_PER_PAGE } from '../../utils/constants';

import { ArticleSuggestionList } from './ArticleSuggestionList';
import styles from './SuggestionList.module.scss';

import {
  SuggestionsLoadingPlaceholder,
  SuggestionsNotFoundPlaceholder,
} from './index';

type SuggestionListProps = {
  /** Is there an error */
  isError?: boolean;
  /** Are suggestions loading */
  isFetching: boolean;
  /** Article suggestions */
  suggestionData?: ArticleSuggestion[];
};
export function SuggestionList({
  isError,
  isFetching,
  suggestionData,
}: SuggestionListProps): JSX.Element {
  const conversationId = useAppSelector(elevioSelectors.conversation.id);
  const conversationStatus = useAppSelector(
    elevioSelectors.conversation.status,
  );
  const tagNames = useAppSelector(elevioSelectors.conversation.tagNames);
  const searchTerm = useAppSelector(elevioSelectors.search.searchTerm);
  const languageId = useAppSelector(
    elevioSelectors.suggestion.selectedLanguageId,
  );

  const dispatch = useAppDispatch();

  const { currentPage, totalPages, startIndex, endIndex, setPage } =
    usePagination({
      totalItems: suggestionData?.length || 0,
      initialPageSize: ARTICLES_PER_PAGE,
    });

  const isComplete = (!!suggestionData || isError) && !isFetching;

  if (!isComplete) {
    return <SuggestionsLoadingPlaceholder />;
  }

  if (isError) {
    throw ElevioError({
      context: 'SuggestionList',
      feature: 'suggestions',
      message: 'Error loading suggestions',
    });
  }

  if (!suggestionData?.length) {
    const isSearching = isValidSearchLength(searchTerm);
    const headline = isSearching ? 'No results found' : 'No suggestions found';
    const description = isSearching
      ? `Try using different keywords and avoid using short words like "and", "as", "what", "is", etc.`
      : 'Try searching for articles above, or try adding tags to the conversation.';

    return (
      <SuggestionsNotFoundPlaceholder
        headline={headline}
        description={description}
      />
    );
  }

  const paginationCurrentPage = currentPage + 1;

  function onPageChange(page: number) {
    setPage(page - 1); // pagination component is not 0-indexed so we need to subtract 1 from the value returned from onPageChange
  }

  const suggestions = suggestionData.slice(startIndex, endIndex + 1);

  const paginationStyles = classnames({
    [styles.paginationWrapper]: suggestions.length > ARTICLES_PER_PAGE,
  });

  function onSelectArticle({
    articleId,
  }: {
    articleId: string;
    articleTitle: string;
  }) {
    if (conversationId) {
      dispatch(
        elevioActions.suggestions.viewArticle({
          articleId,
          ticketStatus: conversationStatus,
          ticketTags: tagNames,
          url: getUrl(),
          ticketId: conversationId.toString(),
          languageId,
        }),
      );
    }
  }

  const paginationMargin =
    totalPages > 1 ? `${sizes.spacer[400]} 0` : `0 0 ${sizes.spacer[400]}`; // only margin-bottom if there no more than 1 page of results

  return (
    <Box>
      <ArticleSuggestionList
        suggestions={suggestions}
        selectedArticleId={null}
        onSelectArticle={onSelectArticle}
        beforeSlot={<NoArticlesFoundBanner />}
      />
      <FlexBox
        margin={paginationMargin}
        justifyContent="center"
        className={paginationStyles}
      >
        <Pagination
          currentPage={paginationCurrentPage}
          pageCount={totalPages}
          onPageChange={onPageChange}
        />
      </FlexBox>
    </Box>
  );
}

function NoArticlesFoundBanner() {
  const unableToFindTaggedSuggestions = useAppSelector(
    elevioSelectors.suggestion.unableToFindTaggedSuggestions,
  );
  if (!unableToFindTaggedSuggestions) return null;

  return (
    <Banner
      variant="info"
      description={
        <>
          No articles were found matching the conversation tags, here are some
          suggestions based on the conversation.
        </>
      }
      className={styles.noArticlesFoundBanner}
    />
  );
}
